/* eslint-disable */

import { Routes } from '@angular/router';
import { LoginComponent } from '@components/app/login';
import { NoContentComponent } from '@components/app/no-content';
import { LogoutComponent } from '@components/app/login/logout.component';
import { AuthGuard } from '@shared/helpers/auth-guard';
import { XVIErrorPageComponent } from '@app/components/app/error-page';

import {
  ROOT_ROUTE,
  DASHBOARD_ROUTE,
  LOGIN_ROUTE,
  LOG_OUT_ROUTE,
  ERROR_PAGE_ROUTE,
  IFRAME_MODE,
  ORDER_MODULE,
  TRANSFER_MODULE,
  STAND_WORKSHEET_MODULE,
  INVENTORY_COUNT_GROUP_MODULE,
  INVENTORY_COUNT_MODULE,
  HISTORICAL_TRANSFER_QUANTITIES_MODULE,
  INCOMPLETE_TRANSFER_MODULE,
  TRANSFER_GROUP_MODULE,
  TRANSFER_GROUP_TEMPLATE_MODULE,
  FILTERED_ORDER_MODULE,
  FILTERED_TRANSFER_MODULE,
  TRANSFER_REQUEST_MODULE,
  HAWKER_PROFILE_MODULE,
  HAWKER_LOAD_MODULE,
  HAWKER_COMMISSARY_ASSIGNMENT_MODULE,
  HAWKER_SESSION_MODULE,
  HAWKER_COMMISSARY_ASSIGNMENT_HISTORY_MODULE,
  HAWKER_SESSION_ADMIN_MODULE,
} from './routes';
import { IframeModeComponent } from '@app/components/app/iframe-mode';
import {
  PermissionGuard,
  PermissionResolver,
} from '@shared/helpers/permission-helpers';
import { LandingPageComponent } from './containers/landing-page/landing-page.component';
import { HomeComponent } from './components/app/home';
import { ExitEditGuard } from './shared/helpers/edit-guard/edit-guard.helper';
import { TranslationGuard } from './shared/helpers/i18n-guard/translation.guard';
import { ROUTES_PERMISSIONS } from './routes-permissions';
import {
  SupportedAppCodes,
  PermissionType,
  ENTITY_TYPES,
  PermissionValue,
} from './shared/types';
import { PERMISSIONS } from './shared/services';

export * from './routes';

const APP_GENERIC_GUARD = [AuthGuard, TranslationGuard];
const ALL_SITES_ACCESS_PERMISSION = {
  type: PermissionType.action,
  app_code: SupportedAppCodes.XPRT,
  name: 'People.AllSitesAccess',
};

export const ROUTES: Routes = [
  {
    path: ROOT_ROUTE,
    component: HomeComponent,
    canActivate: APP_GENERIC_GUARD,
  },
  {
    path: LOGIN_ROUTE,
    component: LoginComponent,
  },
  {
    path: DASHBOARD_ROUTE,
    component: LandingPageComponent,
    canActivate: [...APP_GENERIC_GUARD, PermissionGuard],
    canDeactivate: [ExitEditGuard],
    resolve: { permissions: PermissionResolver },
  },
  {
    path: IFRAME_MODE,
    component: IframeModeComponent,
  },
  {
    path: LOG_OUT_ROUTE,
    component: LogoutComponent,
  },
  {
    path: ERROR_PAGE_ROUTE,
    component: XVIErrorPageComponent,
    canActivate: APP_GENERIC_GUARD,
  },
  {
    path: ORDER_MODULE,
    loadChildren: () =>
      import('./containers/orders/orders.module').then(
        (module) => module.OrdersModule,
      ),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
  },
  {
    path: TRANSFER_MODULE,
    loadChildren: () =>
      import('./containers/transfer/transfer.module').then(
        (m) => m.TransferModule,
      ),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
  },
  {
    path: STAND_WORKSHEET_MODULE,
    loadChildren: () =>
      import('./containers/stand-worksheet/stand-worksheet.module').then(
        (m) => m.StandWorksheetModule,
      ),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
  },
  {
    path: INVENTORY_COUNT_GROUP_MODULE,
    loadChildren: () =>
      import(
        './containers/inventory-counts/inventory-counts-group-page.module'
      ).then((m) => m.InventoryCountsGroupPageModule),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: [
        'inventory-count.View',
        ALL_SITES_ACCESS_PERMISSION,
      ],
    },
  },
  {
    path: INVENTORY_COUNT_MODULE,
    loadChildren: () =>
      import('./containers/inventory-counts/inventory-counts-page.module').then(
        (m) => m.InventoryCountsPageModule,
      ),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
  },
  {
    path: INCOMPLETE_TRANSFER_MODULE,
    loadChildren: () =>
      import(
        './containers/incomplete-transfer/incomplete-transfer.module'
      ).then((m) => m.IncompleteTransferModule),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: ['transfer.View'],
    },
  },
  {
    path: HISTORICAL_TRANSFER_QUANTITIES_MODULE,
    loadChildren: () =>
      import(
        './containers/historical-quantities/historical-quantities.module'
      ).then((m) => m.HistoricalQuantitiesModule),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: ['transfer.View'],
    },
  },
  {
    path: TRANSFER_GROUP_MODULE,
    loadChildren: () =>
      import('./containers/transfer-group/transfer-group.module').then(
        (m) => m.TransferGroupModule,
      ),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: ['transfer.View'],
    },
  },
  {
    path: FILTERED_ORDER_MODULE,
    loadChildren: () =>
      import('./containers/filtered-orders/filtered-orders.module').then(
        (m) => m.FilteredOrderModule,
      ),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: ['order.View'],
    },
  },
  {
    path: FILTERED_TRANSFER_MODULE,
    loadChildren: () =>
      import('./containers/filtered-transfer/filtered-transfer.module').then(
        (m) => m.FilteredTransferModule,
      ),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: ['transfer.View'],
    },
  },
  {
    path: TRANSFER_REQUEST_MODULE,
    loadChildren: () =>
      import('./containers/transfer-request/transfer-request.module').then(
        (m) => m.TransferRequestModule,
      ),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: ['transfer.View'],
    },
  },
  {
    path: ':entity/:id/history',
    loadChildren: () => import('./containers/audit-trail/audit-trail.module').then(m => m.AuditTrailModule),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
  },
  {
    path: TRANSFER_GROUP_TEMPLATE_MODULE,
    loadChildren: () => import('./containers/transfer-group-template/transfer-group-template.module').then(m => m.TransferGroupTemplateModule),
    canActivate: APP_GENERIC_GUARD,
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
  },
  {
    path: HAWKER_PROFILE_MODULE,
    loadChildren: () => import('./containers/hawker-profile/hawker-profile.module').then(m => m.HawkerProfileModule),
    canActivate: [AuthGuard],
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: ROUTES_PERMISSIONS[HAWKER_PROFILE_MODULE],
    },
  },
  {
    path: HAWKER_LOAD_MODULE,
    loadChildren: () => import('./containers/hawker-load/hawker-load.module').then(m => m.HawkerLoadModule),
    canActivate: [AuthGuard],
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: [
        ALL_SITES_ACCESS_PERMISSION,
      ],
    },
  },
  {
    path: HAWKER_COMMISSARY_ASSIGNMENT_MODULE,
    loadChildren: () => import('./containers/hawker-commissary-assignment/hawker-commissary-assignment.module').then(m => m.HawkerCommissaryAssignmentModule),
    canActivate: [AuthGuard],
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: [
        PERMISSIONS.PortalEvents.View,
        PERMISSIONS.PortalSites.View,
        ALL_SITES_ACCESS_PERMISSION,
      ],
    },
  },
  {
    path: HAWKER_SESSION_MODULE,
    loadChildren: () => import('./containers/hawker-session/hawker-session.module').then(m => m.HawkerSessionModule),
    canActivate: [AuthGuard],
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: [
        `${ENTITY_TYPES.HAWKER_SESSION}.${PermissionValue.View}`,
      ],
    },
  },
  {
    path: HAWKER_COMMISSARY_ASSIGNMENT_HISTORY_MODULE,
    loadChildren: () => import('./containers/hawker-commissary-assignment-history/hawker-commissary-assignment-history.module').then(m => m.HawkerCommissaryAssignmentHistoryModule),
    canActivate: [AuthGuard],
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
  },
  {
    path: HAWKER_SESSION_ADMIN_MODULE,
    loadChildren: () => import('./containers/hawker-session-admin/hawker-session-admin.module').then(m => m.HawkerSessionAdminModule),
    canActivate: [AuthGuard],
    resolve: { permissions: PermissionResolver },
    canActivateChild: [PermissionGuard],
    data: {
      requiredPermissions: [
        `${ENTITY_TYPES.HAWKER_CONFIG}.${PermissionValue.Edit}`,
        `${ENTITY_TYPES.HAWKER_CONFIG}.${PermissionValue.View}`,
      ],
    },
  },
  { path: '**', component: NoContentComponent },
];
